import {
  EquipmentType,
  parseEqpTypeToModality,
  parseEquipmentType,
} from '@ifit/equipment-types';
import type { IChallengeEquipmentType } from '@ifit/gateway-challenge-store/models';
import type {
  ISubscriptionType,
  IWorkoutCategory,
  IWorkoutFilter,
  IWorkoutLibraryFilters,
} from '@ifit/gateway-workout-store/models';
import type { WorkoutFilters } from '../models/workout-filters';
import { matchesWorkoutFilters } from './matches-workout-filters';

interface IConsole {
  equipmentType: EquipmentType;
  workoutFilters: WorkoutFilters;
}

const free = 'free' as ISubscriptionType;

interface ISeries {
  mobileEnabled: boolean | null;
  libraryFilters?: { equipmentType: EquipmentType }[];
  workoutFilters: WorkoutFilters;
}

interface IStandardizedWorkoutLibraryFilters {
  equipmentType: EquipmentType;
  categories: IWorkoutCategory[];
}

interface IWorkout {
  isDiscoverable?: boolean;
  subscriptionTypes?: ISubscriptionType[];
  libraryFilters?:
    | IWorkoutLibraryFilters[]
    | IStandardizedWorkoutLibraryFilters[];
  workoutFilters?: IWorkoutFilter[];
}

interface IChallenge {
  equipmentTypes: IChallengeEquipmentType[];
}

export function isWorkoutCompatible(
  console: IConsole,
  workout: IWorkout,
  onlyFreeContent?: boolean,
  skipFreeContentChecks?: boolean,
): boolean {
  // Step 1: Only published content.
  if (!workout?.isDiscoverable) {
    return false;
  }

  // Step 2: Match based on equipment types.
  if (
    !workout.libraryFilters?.some(
      filter =>
        parseEquipmentType(filter.equipmentType) === console.equipmentType,
    )
  ) {
    return false;
  }

  // Step 3: Handle exclusively free content (it's generally ugly content, like pure intervals without a video).
  if (!skipFreeContentChecks) {
    const subscriptionTypes = workout.subscriptionTypes || [];
    if (onlyFreeContent && !subscriptionTypes.includes(free)) {
      if (!subscriptionTypes.includes(free)) {
        return false;
      }
    } else {
      if (subscriptionTypes.length === 1 && subscriptionTypes[0] === free) {
        return false;
      }
    }
  }

  // Step 4: Workout filters.
  return matchesWorkoutFilters(console.workoutFilters, workout.workoutFilters);
}

export function isSeriesCompatible(
  console: IConsole,
  series: ISeries,
  onlyFreeContent?: boolean,
): boolean {
  // Step 1: Only published content.
  if (!series.mobileEnabled) {
    return false;
  }

  // Step 2: Match based on equipment types.
  if (
    !series.libraryFilters?.some(
      filter => filter.equipmentType === console.equipmentType,
    )
  ) {
    return false;
  }

  // Step 3: Handle exclusively free content (it's generally ugly content, like pure intervals without a video).
  if (onlyFreeContent) {
    // Series don't have any free content.
    return false;
  }

  // Step 4: Workout filters.
  return matchesWorkoutFilters(console.workoutFilters, series.workoutFilters);
}

export function isChallengeCompatible(
  console: IConsole,
  challenge: IChallenge,
): boolean {
  return (
    undefined !==
    challenge.equipmentTypes.find(current => {
      let type = current.equipmentType.equipmentTypeCode;
      if (type === 'any' || type === 'all') {
        return true;
      }
      if (type === 'yoga') {
        type = EquipmentType.Strength;
      }
      return (
        parseEquipmentType(type) === console.equipmentType ||
        parseEqpTypeToModality(console.equipmentType) === type
      );
    })
  );
}
